import { ScheduledDriveInterval } from '@/utils/scheduling'
import { ScheduledDriveResponseWithEndpoints } from '@/api/main/types.gen'
import { head } from 'lodash-es'
import { moveItem } from '@/utils/moveItem'
import { getRequirementSubintervalsQueryKey } from '@/api/main'
import { queryClient } from '@/lib/queryClient'
import { RequirementSubintervalsResponse, DriveSubinterval } from '@/api/main/types.gen'

export function getIntervalByDroppableId(
  droppableId: string,
  intervals: Record<string, { past: ScheduledDriveInterval[]; rest: ScheduledDriveInterval[] }>
): ScheduledDriveInterval | undefined {
  const [vehicleId, intervalIndex] = droppableId.split('#', 2)

  // Handle empty column case
  if (intervalIndex === 'empty') {
    return undefined
  }

  const restIntervals = intervals[vehicleId]?.rest || []
  return restIntervals[Number(intervalIndex)]
}

export function calculateScheduleAfterDrive(
  destinationIndex: number,
  destinationDrives: ScheduledDriveResponseWithEndpoints[]
): number | undefined {
  if (destinationIndex === 0) {
    return undefined
  } else if (destinationIndex === destinationDrives.length) {
    return destinationDrives[destinationDrives.length - 1]?.id
  } else if (destinationIndex > 0 && destinationIndex < destinationDrives.length) {
    return destinationDrives[destinationIndex - 1]?.id
  }
  return undefined
}

export function calculateScheduleBeforeDrive(
  destinationIndex: number,
  destinationDrives: ScheduledDriveResponseWithEndpoints[]
): number | undefined {
  // When inserting at position 0, schedule before the first drive
  if (destinationIndex === 0 && destinationDrives.length > 0) {
    return destinationDrives[0]?.id
  }
  return undefined
}

export function handleReorderWithinInterval(
  sourceInterval: ScheduledDriveInterval,
  sourceIndex: number,
  destinationIndex: number,
  reorderDrives: (variables: {
    body: { drive_ids: number[]; interval_start: string; interval_end: string; vehicle_id: string }
  }) => void
) {
  const newList = moveItem(sourceIndex, destinationIndex, sourceInterval.drives)
  const firstDrive = head(newList)
  if (firstDrive) {
    reorderDrives({
      body: {
        drive_ids: newList.map(drive => drive.id),
        interval_start: firstDrive.start_after,
        interval_end: firstDrive.finish_before,
        vehicle_id: sourceInterval.vehicle_id,
      },
    })
  }
}

export function handleReorderToBeginning(
  sourceDrive: ScheduledDriveResponseWithEndpoints,
  destinationInterval: ScheduledDriveInterval,
  driveIntervalsQueryKey: {
    path: {
      billing_period_id: number
      starting_from: string
      ending_at: string
    }
  },
  reorderDrives: (variables: {
    body: { drive_ids: number[]; interval_start: string; interval_end: string; vehicle_id: string }
  }) => void
) {
  setTimeout(() => {
    const queryKey = getRequirementSubintervalsQueryKey(driveIntervalsQueryKey)
    const currentData = queryClient.getQueryData(queryKey) as RequirementSubintervalsResponse | undefined

    if (currentData) {
      const targetInterval = currentData.drive_subintervals.find(
        (it: DriveSubinterval) =>
          it.vehicle_id === destinationInterval.vehicle_id &&
          it.interval.starting_from === destinationInterval.interval.starting_from &&
          it.interval.ending_at === destinationInterval.interval.ending_at
      )

      if (targetInterval && targetInterval.drives.length > 1) {
        const rescheduledDrive = targetInterval.drives.find(drive => drive.id === sourceDrive.id)

        if (rescheduledDrive) {
          // Move it to the beginning
          const reorderedDrives = [
            rescheduledDrive,
            ...targetInterval.drives.filter(drive => drive.id !== rescheduledDrive.id),
          ]

          const firstDrive = head(reorderedDrives)
          if (firstDrive) {
            reorderDrives({
              body: {
                drive_ids: reorderedDrives.map(drive => drive.id),
                interval_start: firstDrive.start_after,
                interval_end: firstDrive.finish_before,
                vehicle_id: targetInterval.vehicle_id,
              },
            })
          }
        }
      }
    }
  }, 100) // Small delay to ensure query cache is updated
}
